import React, { Dispatch, FC, ReactNode, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip as ReactTooltip } from "react-tooltip";

import {
  HomeFilter,
  HomeTab,
} from "../../../factories/works/models/home-enums";
import { WorkModel } from "../../../factories/works/models/work.model";
import {
  WorkTableHeaderItem,
  workTableHeaderItems,
  WorkTableSort,
  WorkTableTitle,
} from "../types/work-table";
import { WorkRow } from "./WorkRow";
import { WorkTableHeader } from "./WorkTableHeader";
import { WorkTasksExpandedTable } from "./WorkTasksExpandedTable";

type Props = {
  works: WorkModel[];
  myWorkHome?: boolean;
  filter?: HomeTab | HomeFilter.OverDue;
  setArchiveWorks?: Dispatch<SetStateAction<WorkModel[]>>;
  archiveWorks?: WorkModel[];
  statusFilter?: string[];
  sortBy?: WorkTableSort;
  sortOrder?: "asc" | "desc";
  onSortChange?: (sortBy: WorkTableSort, sortOrder: "asc" | "desc") => void;
  renderExpandedContent?: (work: WorkModel) => ReactNode;
  showSelection?: boolean;
  headerItems?: WorkTableHeaderItem[];
  currentWorkId?: string;
};

export const WorkTable: FC<Props> = ({
  works,
  myWorkHome,
  filter,
  archiveWorks,
  setArchiveWorks,
  statusFilter,
  sortBy,
  sortOrder = "asc",
  onSortChange,
  renderExpandedContent,
  showSelection = true,
  headerItems = workTableHeaderItems,
  currentWorkId,
}) => {
  const navigate = useNavigate();
  const showTaskProgress = headerItems.some(
    (item) => item.title === WorkTableTitle.Progress,
  );
  const showTimeBudget = headerItems.some(
    (item) => item.title === WorkTableTitle.TimeBudget,
  );
  const showRepeat = headerItems.some(
    (item) => item.sort === WorkTableSort.Repeats,
  );
  const showRole = headerItems.some(
    (item) => item.title === WorkTableTitle.Role,
  );
  const columnsCount = headerItems.length + 1;

  if (!works?.length) {
    return (
      <div className={"mb-5 w-full text-center font-semibold"}>
        There are no work items
      </div>
    );
  }

  return (
    <>
      <table className={"w-full"}>
        <WorkTableHeader
          works={works}
          archiveWorks={archiveWorks}
          setArchiveWorks={setArchiveWorks}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={onSortChange}
          showSelection={showSelection}
          headerItems={headerItems}
        />

        <tbody>
          {works.map((work, index) => (
            <WorkRow
              key={work.workId}
              work={work}
              index={index}
              worksCount={works.length}
              myWorkHome={myWorkHome}
              filter={filter}
              archiveWorks={archiveWorks}
              setArchiveWorks={setArchiveWorks}
              statusFilter={statusFilter}
              showSelection={showSelection}
              showTaskProgress={showTaskProgress}
              showTimeBudget={showTimeBudget}
              showRole={showRole}
              showRepeat={showRepeat}
              columnsCount={columnsCount}
              isCurrent={
                !!currentWorkId &&
                work.workId?.toLowerCase() === currentWorkId.toLowerCase()
              }
              onNavigateToWork={(workId) => navigate(`/work/${workId}`)}
              onNavigateToContact={navigate}
              renderExpandedContent={
                renderExpandedContent ||
                ((currentWork) => (
                  <WorkTasksExpandedTable
                    workId={currentWork.workId}
                    workTypeId={currentWork.workTypeId}
                    workStatusId={currentWork.jobStatusId}
                  />
                ))
              }
            />
          ))}
        </tbody>
      </table>

      <ReactTooltip
        id={"repeat-type-tooltip"}
        place={"top"}
        className={"!rounded-[8px] !p-2 !text-sm"}
      />
    </>
  );
};
